import type { BillingOrganizationSummary } from '../../../types/adminBilling';
import { BillingSetupChecklist, type BillingSetupStep } from './BillingSetupChecklist';
import { formatMoney } from './billingFormat';
import { BILLING_SUBSCRIPTION_STATUSES } from './billingSetupState';
import { AdminPanel } from '../AdminPage';

interface BillingSetupGuideProps {
  /** Null while the summary is still loading; the guide renders nothing rather than guessing. */
  summary: BillingOrganizationSummary | null;
}

/**
 * What this organization still needs before anybody is billed, in the order the pieces depend on
 * each other.
 *
 * **One path: every organization is billed through Stripe.** This system still has the machinery to
 * raise and chase invoices itself, and an organization not yet moved across falls back to it, but
 * it is no longer offered as a choice. Presenting two ways to bill meant an operator had to decide
 * which one they were doing before anything on the screen made sense, and nothing told them a
 * decision was being made.
 *
 * **Why this exists at all.** The pieces were on the page already, as four panels to be assembled
 * into an order. Nothing said the plan had to come first, though the Stripe customer id is stored
 * ON the plan row so every Stripe call answers 409 without one. Nothing said the billing contact is
 * where the Stripe customer takes its invoice email from. And nothing said that a Stripe customer
 * with no subscription is billed by nobody: this system stands down the moment a customer is
 * linked, and Stripe raises invoices only while a subscription is live. That state is silent, reads
 * as healthy on every other panel, and is the most expensive thing here to get wrong.
 */
export const BillingSetupGuide = ({ summary }: BillingSetupGuideProps) => {
  if (!summary) return null;

  const { plan, isStripeManaged, activeToContactCount } = summary;
  const hasPlan = plan !== null;
  const hasContact = activeToContactCount > 0;

  const subscriptionLive =
    plan !== null
    && plan.stripeSubscriptionId !== null
    && plan.stripeSubscriptionStatus !== null
    && BILLING_SUBSCRIPTION_STATUSES.has(plan.stripeSubscriptionStatus);

  const steps: BillingSetupStep[] = [
    {
      key: 'plan',
      name: 'Billing plan',
      state: hasPlan ? 'done' : 'todo',
      detail:
        hasPlan && plan
          ? `${formatMoney(plan.amountCents, plan.currency)} ${plan.interval.toLowerCase()}`
          : 'Not set. The Stripe customer is recorded against it, so nothing else here works without one.',
      action:
        'Add a plan in the Plan panel below. Its amount and interval become the subscription defaults.',
    },
    {
      key: 'contact',
      name: 'Billing contact',
      state: hasContact ? 'done' : 'todo',
      detail: hasContact
        ? `${activeToContactCount} active To contact${activeToContactCount === 1 ? '' : 's'}`
        : 'None. The Stripe customer takes its invoice email from here, so Stripe would have nobody to send to.',
      action: 'Add an accounts payable address in the Contacts panel below.',
    },
    {
      key: 'customer',
      name: 'Stripe customer',
      state: isStripeManaged ? 'done' : 'todo',
      detail: isStripeManaged
        ? 'Linked. Stripe holds this organization and does its chasing.'
        : 'Not linked. Create one, or link a customer that already exists at Stripe.',
      action: 'Create or link it in the Stripe panel below.',
    },
    {
      key: 'method',
      name: 'Saved payment method',
      state: !plan || plan.defaultPaymentMethodKind === 'None' ? 'optional' : 'done',
      detail:
        !plan || plan.defaultPaymentMethodKind === 'None'
          ? 'None on file. Only needed if Stripe should charge automatically rather than email an invoice.'
          : plan.defaultPaymentMethodLabel ?? 'On file.',
    },
    {
      key: 'subscription',
      name: 'Recurring invoice',
      state: subscriptionLive ? 'done' : 'todo',
      detail: subscriptionLive
        ? 'Stripe is invoicing this organization each period.'
        : 'Not set up. This is the thing that actually sends the invoice, every period, so until it exists nobody is billed.',
      action: 'Set it up in the Stripe panel below.',
    },
  ];

  /*
    The consequence no single step shows: an organization already handed to Stripe with no
    subscription behind it. Every other panel reads as settled, because a plan is in place and
    nothing is overdue - and nothing is overdue because nothing is being raised at all.
  */
  const warning =
    isStripeManaged && !subscriptionLive
      ? 'Stripe holds this organization, so this system raises nothing for it, and with no subscription neither does Stripe. Nobody is being billed.'
      : null;

  const outstanding = steps.some((step) => step.state === 'todo');

  return (
    // The wrapper carries the test id, which AdminPanel does not pass through.
    <AdminPanel data-testid="billing-setup-guide"
      title={outstanding ? 'Finish setting this organization up' : 'Billing setup'}
      titleId="billing-setup-title"
      description={
        outstanding
          ? 'In order. Each step is recorded against the one above it, and Stripe does the billing.'
          : 'Everything needed is in place, and Stripe is billing this organization.'
      }
    >
      <BillingSetupChecklist steps={steps} warning={warning} />
    </AdminPanel>
  );
};
